import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MeterService, MeterData } from './../../services/meter.service';
import { keycloakInstance } from '../../keycloak.config';
import { MatIconModule } from '@angular/material/icon';
import { ValidationService } from '../../services/validation.service';
import { Subscription } from 'rxjs';

// Interface for business activity code (KAD)
interface BusinessActivityCode {
  description: string;
  is_category_d: boolean;
  is_excluded: boolean;
  kad: string;
}

// Interface for subsidy info received from API
interface SubsidyInfo {
  annual_turnover: number;
  applicable_ratio?: number;
  business_activity_code: BusinessActivityCode;
  consumption_kwh: number;
  deminimis_limit: boolean;
  description: string;
  meter_id: string;
  status: string; // Can be 'success', 'rejected', or 'pending'
  subsidy_per_kwh: number;
  supply_type: string;
  total_subsidy: number;
}

// Interface for API response items
interface SubsidyResponseItem {
  meter_id: string;
  subsidy_info: SubsidyInfo;
  submit_number?: string;
}

// Interface for meter details to display
interface MeterDetails {
  id: string;
  value: string;
  eligible: boolean;
  explanation: string;
  subsidyAmount: string;
  status: string; // Can be 'success', 'rejected', or 'pending'
  // Fields
  kad: string;
  kadDescription: string;
  consumption: number;
  subsidyPerKwh: number;
  applicable_ratio?: number;
}

@Component({
  selector: 'app-check-eligibility',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './check-eligibility.component.html',
  styleUrl: './check-eligibility.component.scss'
})
export class CheckEligibilityComponent implements OnInit, OnDestroy {
  // Property to store the meters data
  meters: MeterData = {};
  title='Έλεγχος Επιλεξιμότητας';
  // Property to store the validation results
  validationResults: SubsidyResponseItem[] = [];
  // Property to store processed meter details
  meterDetails: MeterDetails[] = [];
  hasError: boolean = false;
  // Loading indicator
  isLoading: boolean = true;
  // Error message
  errorMessage: string = '';
  requestID: string = '';
  // Flag to control popup visibility
  showPopup: boolean = false;
  
  // For UI compatibility, set all to false and don't use
  hasPreviousApplications = false;
  showRecallDialog = false;
  showSuccessDialog = false;
  successMessage = '';
  loadingProgress = false;
  showImmediateRecallPrompt = false;

  constructor(
    private router: Router,
    private meterService: MeterService,
    private validationService: ValidationService
  ) {}

  ngOnInit(): void {
    // Get meter data from service and directly fetch validation results
    this.meterService.currentMeters.subscribe((data: MeterData) => {
      this.meters = data;
      console.log('Retrieved meters in CheckEligibilityComponent:', this.meters);
      
      // Check if meters data exists
      if (Object.keys(this.meters).length === 0) {
        console.log('No meters selected. Redirecting to input-meters...');
        this.router.navigate(['/input-meters']);
        return;
      }
      
      // Always fetch validation results directly
      this.fetchValidationResults();
    });
  }

  ngOnDestroy(): void {
    // Nothing to clean up
  }

  // Format date for display (kept for compatibility)
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('el-GR');
    } catch (e) {
      return dateString;
    }
  }

  // Main method to fetch validation results
  fetchValidationResults(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.hasError = false;

    // Get the stored parameters that were prepared in InputMetersComponent
    const params = this.meterService.getStoredParams();
    
    // Check if parameters exist
    if (!params || params.keys().length === 0) {
      this.isLoading = false;
      this.errorMessage = 'Δεν βρέθηκαν παράμετροι για τις επιλεγμένες παροχές.';
      this.hasError = true;
      this.title='Η ΑΙΤΗΣΗ ΣΑΣ';
      console.error('No parameters found for validation');
      return;
    }

    console.log('Making API call with stored parameters:', params.toString());

    // Make the actual API call with the stored parameters
    this.meterService.validateSelectedMeters(params).subscribe(
      (results: any) => {
        this.isLoading = false;
    
        // Check if we received an error response object
        if (results && results.status === 'error') {
          this.errorMessage = results.description || 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των αποτελεσμάτων.';
          this.hasError = true;
          this.title='Η ΑΙΤΗΣΗ ΣΑΣ';
          console.error('API returned error:', results);
          return;
        }
    
        if (results && results.length > 0) {
          // Extract and store the submit_number if present
          const submitNumberEntry = results.find((item: any) => item.hasOwnProperty('submit_number'));
          if (submitNumberEntry) {
            this.requestID = submitNumberEntry.submit_number;
            console.log('Request ID from validation results:', this.requestID);
          }
          
          // Filter out the submit_number entry and keep only the meter entries
          this.validationResults = results.filter((item: any) => item.hasOwnProperty('meter_id'));
          console.log('Retrieved validation results:', this.validationResults);
          
          if (this.validationResults.length > 0) {
            this.processValidationResults();
          } else {
            this.errorMessage = 'Δεν βρέθηκαν αποτελέσματα για τις επιλεγμένες παροχές.';
            this.hasError = true;
            this.title='Η ΑΙΤΗΣΗ ΣΑΣ';
            console.log('No validation results available');
          }
        } else {
          this.errorMessage = 'Δεν βρέθηκαν αποτελέσματα για τις επιλεγμένες παροχές.';
          this.hasError = true;
          this.title='Η ΑΙΤΗΣΗ ΣΑΣ';
          console.log('No validation results available');
        }
      },
      (error) => {
        this.isLoading = false;
        this.hasError = true;
        this.title='Η ΑΙΤΗΣΗ ΣΑΣ';
    
        // Check if the error response contains the specific error structure
        if (error && error.error) {
          // The error might be in error.error due to how HttpClient wraps errors
          if (error.error.status === 'error' && error.error.description) {
            this.errorMessage = error.error.description;
            console.error('API error with description:', error.error);
            return;
          }
        }
    
        // Default error message if we can't extract a specific one from the response
        this.errorMessage = 'Παρουσιάστηκε σφάλμα κατά την ανάκτηση των αποτελεσμάτων.';
        console.error('Error fetching validation results:', error);
      }
    );
  }

  // Process validation results into meter details for display
  processValidationResults(): void {
    this.meterDetails = [];
    const processedMeters = new Map<string, MeterDetails>();

    // Process each response item
    for (const item of this.validationResults) {
      // Skip items that don't have meter_id (like submit_number)
      if (!item.meter_id || !item.subsidy_info) {
        continue;
      }

      const meterId = item.meter_id;
      const subsidyInfo = item.subsidy_info;
      
      // Determine if meter is eligible based on status
      const isEligible = subsidyInfo.status === 'success';
      
      // Format subsidy amount - show "-" for pending status
      let formattedSubsidy = '0';
      
      if (subsidyInfo.status === 'pending') {
        formattedSubsidy = '-';
      } else if (subsidyInfo.total_subsidy) {
        formattedSubsidy = subsidyInfo.total_subsidy.toFixed(2).replace('.', ',');
      }
      
      // Extract KAD information if available
      let kad = '-';
      let kadDescription = '-';
      if (subsidyInfo.business_activity_code && typeof subsidyInfo.business_activity_code === 'object') {
        kad = subsidyInfo.business_activity_code.kad || '-';
        kadDescription = subsidyInfo.business_activity_code.description || '-';
      }
      
      // Create meter details with fields
      const meterDetail: MeterDetails = {
        id: meterId,
        value: meterId,
        eligible: isEligible,
        explanation: subsidyInfo.description || 'Δεν υπάρχει διαθέσιμη επεξήγηση.',
        subsidyAmount: formattedSubsidy,
        status: subsidyInfo.status || 'unknown',
        kad: kad,
        kadDescription: kadDescription,
        consumption: subsidyInfo.consumption_kwh || 0,
        subsidyPerKwh: subsidyInfo.subsidy_per_kwh || 0,
        applicable_ratio: subsidyInfo.applicable_ratio
      };

      // Add to processed map
      processedMeters.set(meterId, meterDetail);
    }

    // Convert map to array for template use
    this.meterDetails = Array.from(processedMeters.values());
    
    // If no results were processed, set error message
    if (this.meterDetails.length === 0) {
      this.errorMessage = 'Δεν βρέθηκαν πληροφορίες επιδότησης για τις επιλεγμένες παροχές.';
    }
  }
  
  // Helper method to get the processed meter details for the template
  getMetersArray(): MeterDetails[] {
    return this.meterDetails;
  }

  // Method to go back to previous screen
  goBack(): void {
    this.router.navigate(['input-meters']);
  }

  // Logout Keycloak
  logoutKeycloak() {
    if (keycloakInstance) {
      keycloakInstance.logout({
        redirectUri: window.location.origin,
        logoutMethod: 'POST'
      });
    }
  }
  
  // Method to show the popup
  showConfirmationPopup(): void {
    this.logoutcomplete();
  }

  // Logout completion
  logoutcomplete() {
    this.validationService.completeResults().subscribe(res => {
      this.requestID = res.aa_number;
      this.showPopup = true;
    });
  }

  // Method to proceed to the next step in the flow
  proceedToNext(): void {
    this.showConfirmationPopup();
  }
  
  // These methods are kept for compatibility with the HTML template
  // but are effectively empty as we don't use recall functionality
  
  closeRecallDialog(): void {
    this.showRecallDialog = false;
  }
  
  closeSuccessDialog(): void {
    this.showSuccessDialog = false;
  }
  
  openRecallDialog(): void {
    // No-op
  }
  
  closeImmediateRecallPrompt(recallConfirmed: boolean): void {
    this.showImmediateRecallPrompt = false;
  }
}